import React from 'react'
import { observer } from 'mobx-react'
import classNames from 'classnames'

import { formatDateTime } from '@atheneum/api/dist/lib'

import CalendarIcon from '@components/Icon/Calendar'
import CancelIcon from '@components/Icon/Cancel'
import ConfirmIcon from '@components/Icon/Confirm'
import InterviewIcon from '@components/Icon/Interview'

const StatusAction = observer(({ statusAction, epl, modalStore, inModal }) => {
  const classes = classNames({
    epl__action: !inModal,
    button: inModal,
    'button--primary': inModal && statusAction.primary,
    'button--secondary': inModal && !statusAction.primary
  })

  if (inModal) {
    return (
      <button disabled className={classes}>
        {statusAction.name}
      </button>
    )
  } else {
    return (
      <div className={classes}>
        {statusAction.icon}
        {statusAction.name}
      </div>
    )
  }
})

const ActionStatusMap = observer(({ staticData, epl, inModal }) => {
  const {
    EPL_STATUSES,
    EPL_PRELIMINARY_TAGS,
    EPL_STATUS_COMMENTS
  } = staticData.CONST
  let actions = null

  if (epl.eplStatusId === EPL_STATUSES.INTERVIEWED) {
    actions = generateInterviewedActions({ EPL_STATUSES }, epl)
  }

  if (epl.eplStatusId === EPL_STATUSES.SCHEDULED) {
    actions = generateScheduledActions({ EPL_STATUSES }, epl)
  }

  if (epl.eplStatusId === EPL_STATUSES.REJECTED) {
    actions = generateNotSelectedActions({ EPL_STATUSES }, epl)
  }

  if (epl.eplStatusId === EPL_STATUSES.REQUESTED) {
    actions = generateAcceptedActions(
      { EPL_STATUSES, EPL_STATUS_COMMENTS },
      epl
    )
  }

  if (epl.eplStatusId === EPL_STATUSES.NOT_INTERVIEWED) {
    actions = generateCanceledActions({ EPL_STATUSES }, epl)
  }

  if (epl.eplStatusId === EPL_STATUSES.SUBMITTED) {
    actions = generateSubmittedActions(
      { EPL_STATUSES, EPL_PRELIMINARY_TAGS },
      epl
    )
  }

  if (epl.eplStatusId < EPL_STATUSES.SUBMITTED) {
    actions = generatePreliminaryActions(
      { EPL_STATUSES, EPL_PRELIMINARY_TAGS },
      epl
    )
  }

  const content =
    actions &&
    actions.map(statusAction => (
      <StatusAction
        key={statusAction.name}
        statusAction={statusAction}
        inModal={inModal}
      />
    ))

  const classes = inModal
    ? 'epl__content epl__actions epl__actions--in-modal'
    : 'epl__content epl__actions'

  return <div className={classes}>{content}</div>
})

export default ActionStatusMap

function generateInterviewedActions ({ EPL_STATUSES }, epl) {
  const interviewDate = epl.interviewDate ? (
    <span>
      Interviewed on <b>{epl.interviewDate}</b>
    </span>
  ) : (
    'Interviewed'
  )

  return [
    {
      action: false,
      id: EPL_STATUSES.INTERVIEWED,
      name: interviewDate,
      icon: <InterviewIcon />
    }
  ]
}

function generateScheduledActions ({ EPL_STATUSES }, epl) {
  let scheduleTime = ''
  const hasScheduleStartTime =
    epl.scheduledAvailability &&
    epl.scheduledAvailability.schedule &&
    epl.scheduledAvailability.schedule.startTime

  if (hasScheduleStartTime) {
    scheduleTime = formatDateTime(epl.scheduledAvailability.schedule.startTime)
  }

  const scheduledName = scheduleTime ? (
    <span>
      Scheduled on <b>{scheduleTime}</b>
    </span>
  ) : (
    'Scheduled'
  )

  return [
    {
      action: false,
      id: EPL_STATUSES.SCHEDULED,
      name: scheduledName,
      icon: <CalendarIcon />
    },
    {
      action: true,
      name: 'Cancel consultation',
      icon: <CancelIcon />,
      primary: false
    }
  ]
}

function generateNotSelectedActions ({ EPL_STATUSES }, epl) {
  return [
    {
      action: false,
      name: 'Expert rejected',
      icon: <CancelIcon grey />
    },
    {
      action: true,
      name: 'Request scheduling',
      icon: <ConfirmIcon />,
      primary: true
    }
  ]
}

function generateAcceptedActions ({ EPL_STATUSES }, epl) {
  return [
    {
      action: false,
      name: 'Scheduling requested',
      icon: <CalendarIcon />
    },
    {
      action: true,
      name: 'Cancel request',
      icon: <CancelIcon />,
      primary: false
    }
  ]
}

function generateCanceledActions ({ EPL_STATUSES }, epl) {
  return [
    {
      action: false,
      name: 'Interview cancelled',
      icon: <CancelIcon grey />
    }
  ]
}

function generateSubmittedActions ({ EPL_STATUSES }, epl) {
  return [
    {
      action: true,
      name: 'Request scheduling',
      icon: <ConfirmIcon />,
      primary: true
    },
    {
      action: true,
      name: 'Reject profile',
      icon: <CancelIcon />,
      primary: false
    }
  ]
}

function generatePreliminaryActions (
  { EPL_STATUSES, EPL_PRELIMINARY_TAGS },
  epl
) {
  const preliminaryRejectAction = {
    action: true,
    name: 'Cancel request',
    icon: <CancelIcon />,
    primary: false
  }

  if (
    epl.eplPreliminaryTagId === EPL_PRELIMINARY_TAGS.PRELIMINARY_LIST_PENDING
  ) {
    return generateSubmittedActions({ EPL_STATUSES }, epl)
  }

  if (epl.eplPreliminaryTagId === EPL_PRELIMINARY_TAGS.PRELIMINARILY_APPROVED) {
    return [
      {
        action: false,
        name: 'Scheduling requested',
        icon: <CalendarIcon />
      },
      preliminaryRejectAction
    ]
  }

  if (epl.eplPreliminaryTagId === EPL_PRELIMINARY_TAGS.PRELIMINARY_REJECTED) {
    return generateNotSelectedActions({ EPL_STATUSES }, epl)
  }

  return []
}
